
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Trash2, Plus, Save, Send, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ReactCountryFlag from 'react-country-flag';
import { countryCodeToIso } from '@/utils/countryCodes';

// Define a type for our call entries
interface CallEntry {
  id: string;
  name: string;
  phoneNumber: string;
  countryCode: string;
  industry: string;
  businessName: string;
  called: boolean;
}

// Local storage key for session
const LOCAL_STORAGE_KEY = 'outbound-call-entries';

const InternalOutboundCalling: React.FC = () => {
  const [entries, setEntries] = useState<CallEntry[]>([]);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+64'); // Default to NZ
  const [industry, setIndustry] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch existing entries on component mount
  useEffect(() => {
    // First try to load from local storage
    const savedEntries = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedEntries) {
      try {
        const parsedEntries = JSON.parse(savedEntries);
        setEntries(parsedEntries);
        
        toast({
          title: 'Entries Loaded',
          description: 'Call entries have been loaded from your local session',
          variant: 'default'
        });
      } catch (error) {
        console.error('Error parsing local storage entries:', error);
        // If there's an error parsing, fall back to fetching from Supabase
        fetchEntries();
      }
    } else {
      // If no local storage entries, fetch from Supabase
      fetchEntries();
    }
  }, []);

  // Save entries to local storage whenever entries change
  useEffect(() => {
    if (entries.length > 0) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(entries));
      } catch (error) {
        console.error('Error saving to local storage:', error);
        toast({
          title: 'Storage Error',
          description: 'Failed to save entries to local session',
          variant: 'destructive'
        });
      }
    }
  }, [entries]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('outbound_call_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      if (data) {
        const formattedEntries: CallEntry[] = data.map(entry => ({
          id: entry.id,
          name: entry.name,
          phoneNumber: entry.phone_number.replace(entry.country_code, ''),
          countryCode: entry.country_code,
          industry: entry.industry,
          businessName: entry.business_name,
          called: entry.called
        }));
        setEntries(formattedEntries);
        
        // Save fetched entries to local storage
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formattedEntries));
      }
    } catch (error) {
      toast({
        title: 'Error fetching entries',
        description: 'Unable to load call entries',
        variant: 'destructive'
      });
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNewEntry = () => {
    // Generate a temporary client-side ID for the new entry
    const tempId = `temp-${Date.now()}`;
    const newEntry: CallEntry = {
      id: tempId,
      name,
      phoneNumber,
      countryCode,
      industry,
      businessName,
      called: false
    };

    setEntries(prev => [newEntry, ...prev]);
    
    // Reset form
    setName('');
    setPhoneNumber('');
    setIndustry('');
    setBusinessName('');
  };

  const saveEntry = async (entry: CallEntry) => {
    try {
      const sanitizedPhoneNumber = entry.phoneNumber.replace(/[^\d]/g, '');
      
      // Basic validation
      if (
        !entry.name.trim() || 
        !sanitizedPhoneNumber || 
        !entry.industry.trim() || 
        !entry.businessName.trim()
      ) {
        toast({
          title: 'Validation Error',
          description: 'All fields are required',
          variant: 'destructive'
        });
        return;
      }

      if (sanitizedPhoneNumber.length < 8 || sanitizedPhoneNumber.length > 15) {
        toast({
          title: 'Invalid Phone Number',
          description: 'Please enter a valid phone number',
          variant: 'destructive'
        });
        return;
      }

      const { data, error } = await supabase
        .from('outbound_call_entries')
        .insert({
          name: entry.name,
          phone_number: `${entry.countryCode}${sanitizedPhoneNumber}`,
          country_code: entry.countryCode,
          industry: entry.industry,
          business_name: entry.businessName,
          called: entry.called
        })
        .select();

      if (error) throw error;

      // Update the entry in state with the real ID from the database
      if (data && data.length > 0) {
        setEntries(prev => 
          prev.map(e => 
            e.id === entry.id 
              ? { 
                  ...entry, 
                  id: data[0].id,
                  phoneNumber: sanitizedPhoneNumber
                } 
              : e
          )
        );

        toast({
          title: 'Entry Saved',
          description: 'The call entry has been saved successfully',
          variant: 'default'
        });
      }
    } catch (error) {
      toast({
        title: 'Error Saving Entry',
        description: 'Unable to save the call entry',
        variant: 'destructive'
      });
      console.error('Error saving entry:', error);
    }
  };

  const deleteEntry = async (id: string) => {
    try {
      // Check if this is a temporary ID (not saved to DB yet)
      if (id.startsWith('temp-')) {
        setEntries(prev => prev.filter(e => e.id !== id));
        return;
      }

      const { error } = await supabase
        .from('outbound_call_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setEntries(prev => prev.filter(e => e.id !== id));
      
      toast({
        title: 'Entry Deleted',
        description: 'The call entry has been deleted',
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Error Deleting Entry',
        description: 'Unable to delete the call entry',
        variant: 'destructive'
      });
      console.error('Error deleting entry:', error);
    }
  };

  const toggleCalled = async (entry: CallEntry) => {
    try {
      // Don't try to update temporary entries
      if (entry.id.startsWith('temp-')) {
        toast({
          title: 'Save Required',
          description: 'Please save the entry before marking it as called',
          variant: 'destructive'
        });
        return;
      }

      const updatedValue = !entry.called;
      
      const { error } = await supabase
        .from('outbound_call_entries')
        .update({ called: updatedValue })
        .eq('id', entry.id);

      if (error) throw error;

      setEntries(prev => 
        prev.map(e => 
          e.id === entry.id 
            ? { ...e, called: updatedValue } 
            : e
        )
      );
    } catch (error) {
      toast({
        title: 'Error Updating Status',
        description: 'Unable to update the call status',
        variant: 'destructive'
      });
      console.error('Error updating status:', error);
    }
  };

  const submitAllEntries = async () => {
    // Find all temporary entries that need to be saved
    const tempEntries = entries.filter(entry => entry.id.startsWith('temp-'));
    
    if (tempEntries.length === 0) {
      toast({
        title: 'No New Entries',
        description: 'There are no new entries to submit',
        variant: 'default'
      });
      return;
    }

    setSubmitting(true);
    
    try {
      // Process entries one by one
      for (const entry of tempEntries) {
        const sanitizedPhoneNumber = entry.phoneNumber.replace(/[^\d]/g, '');
        
        // Skip invalid entries
        if (
          !entry.name.trim() || 
          !sanitizedPhoneNumber || 
          sanitizedPhoneNumber.length < 8 || 
          sanitizedPhoneNumber.length > 15 ||
          !entry.industry.trim() || 
          !entry.businessName.trim()
        ) {
          continue;
        }

        // Save to Supabase
        const { data, error } = await supabase
          .from('outbound_call_entries')
          .insert({
            name: entry.name,
            phone_number: `${entry.countryCode}${sanitizedPhoneNumber}`,
            country_code: entry.countryCode,
            industry: entry.industry,
            business_name: entry.businessName,
            called: entry.called
          })
          .select();

        if (error) {
          console.error('Error saving entry:', error);
          continue;
        }

        // Update entry in state if saved successfully
        if (data && data.length > 0) {
          setEntries(prev => 
            prev.map(e => 
              e.id === entry.id 
                ? { 
                    ...entry, 
                    id: data[0].id,
                    phoneNumber: sanitizedPhoneNumber
                  } 
                : e
            )
          );
        }
      }

      // Fetch fresh data after bulk save
      await fetchEntries();
      
      toast({
        title: 'Entries Submitted',
        description: 'All new call entries have been saved successfully',
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Error Submitting Entries',
        description: 'Some entries may not have been saved',
        variant: 'destructive'
      });
      console.error('Error in bulk save:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Clear local storage and reset to database state
  const resetToDatabase = async () => {
    const confirmed = window.confirm(
      "This will clear all unsaved entries from your local session and reload from the database. Continue?"
    );
    
    if (confirmed) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      await fetchEntries();
      
      toast({
        title: 'Session Reset',
        description: 'Call entries have been reloaded from the database',
        variant: 'default'
      });
    }
  };

  const countryCodesData = React.useMemo(() => {
    return Object.entries(countryCodeToIso).map(([code, iso]) => ({
      code,
      iso,
    })).sort((a, b) => a.code.localeCompare(b.code));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 mb-4 text-3xl font-bold font-ibm">
            <span className="text-brand-navy">Think</span>
            <span className="text-brand-teal">Tomorrow</span>
            <span className="text-brand-navy uppercase">.ai</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Internal Outbound Calling System
          </h1>
          <p className="text-gray-600">
            Add and manage outbound call entries for your team
          </p>
        </div>

        <div className="bg-white shadow-md rounded-lg p-8 mb-8">
          <h2 className="text-xl font-semibold mb-4">Add New Call Entry</h2>
          
          {/* New spreadsheet-like layout */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="name" className="block mb-2">Contact Name</Label>
            </div>
            <div>
              <Label htmlFor="industry" className="block mb-2">Industry</Label>
            </div>
            <div>
              <Label htmlFor="businessName" className="block mb-2">Business Name</Label>
            </div>
            <div>
              <Label htmlFor="phone" className="block mb-2">Phone Number</Label>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Input 
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter contact name"
              />
            </div>
            
            <div>
              <Input 
                id="industry"
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Enter industry"
              />
            </div>
            
            <div>
              <Input 
                id="businessName"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Enter business name"
              />
            </div>
            
            <div>
              <div className="flex gap-2">
                <Select
                  value={countryCode}
                  onValueChange={setCountryCode}
                >
                  <SelectTrigger className="w-[80px]">
                    <div className="flex items-center justify-center">
                      {countryCode && countryCodeToIso[countryCode] && (
                        <ReactCountryFlag
                          countryCode={countryCodeToIso[countryCode]}
                          svg
                          style={{ width: '1.2em', height: '1.2em' }}
                        />
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {countryCodesData.map((country) => (
                      <SelectItem 
                        key={country.code} 
                        value={country.code}
                        className="flex items-center gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          {country.iso && (
                            <ReactCountryFlag
                              countryCode={country.iso}
                              svg
                              style={{ width: '1.2em', height: '1.2em' }}
                            />
                          )}
                          <span>{country.code}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter phone number"
                  className="flex-1"
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Please enter phone number without the initial 0.
              </p>
            </div>
          </div>

          <Button 
            onClick={addNewEntry} 
            className="mt-6 w-full"
            disabled={!name || !phoneNumber || !industry || !businessName}
          >
            <Plus className="mr-2 h-4 w-4" /> Add New Entry
          </Button>
        </div>

        <div className="bg-white shadow-md rounded-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Call Entries</h2>
            <div className="flex items-center">
              <p className="text-sm text-gray-500 mr-3">
                {entries.filter(e => e.id.startsWith('temp-')).length} unsaved entries in session
              </p>
              <Button
                onClick={resetToDatabase}
                variant="outline"
                size="sm"
                className="flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-1" /> Reset Session
              </Button>
            </div>
          </div>
          
          {loading ? (
            <p className="text-center py-8">Loading entries...</p>
          ) : entries.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No call entries yet. Add some above.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact Name</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Business Name</TableHead>
                      <TableHead>Phone Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} className={entry.called ? 'bg-gray-50' : ''}>
                        <TableCell>{entry.name}</TableCell>
                        <TableCell>{entry.industry}</TableCell>
                        <TableCell>{entry.businessName}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {countryCodeToIso[entry.countryCode] && (
                              <ReactCountryFlag
                                countryCode={countryCodeToIso[entry.countryCode]}
                                svg
                                style={{ width: '1.2em', height: '1.2em' }}
                              />
                            )}
                            <span>{entry.countryCode} {entry.phoneNumber}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => toggleCalled(entry)}
                            className={`px-3 py-1 rounded text-sm ${
                              entry.called 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {entry.called ? 'Called' : 'Not Called'}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {entry.id.startsWith('temp-') && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => saveEntry(entry)}
                                className="h-8 w-8 p-0"
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => deleteEntry(entry.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Submit All Entries Button */}
              <div className="mt-6">
                <Button
                  onClick={submitAllEntries}
                  disabled={submitting || !entries.some(e => e.id.startsWith('temp-'))}
                  className="w-full"
                  variant="secondary"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {submitting ? 'Submitting...' : 'Submit All New Call Entries'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternalOutboundCalling;
